import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import {
  createNote as apiCreateNote,
  deleteNote as apiDeleteNote,
  fetchNoteById,
  updateNote as apiUpdateNote,
} from '@/api/resources/note.api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import {
  EObservationTimelineChange,
  invalidateObservationTimelineQueries,
} from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { useRefreshSetupChecklist } from '@/composables/setupChecklist/useRefreshSetupChecklist';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useQueryClient } from '@tanstack/vue-query';
import { invalidateGrowthCtaQueries } from '../growth/growthCtaQueryInvalidation';

export enum ENoteCreationSource {
  Composer = 'composer',
}

interface ICreateNoteOptions {
  noteCreationSource?: ENoteCreationSource;
  suppressSuccessToast?: boolean;
}

interface INoteComposable {
  createNote: (
    dto: Components.Schemas.CreateNoteDto,
    options?: ICreateNoteOptions,
  ) => Promise<Components.Schemas.NoteDto>;
  updateNote: (noteId: number, dto: Components.Schemas.UpdateNoteDto) => Promise<Components.Schemas.NoteDto>;
  deleteNote: (noteId: number) => Promise<void>;
  getNoteById: (noteId: number) => Promise<Components.Schemas.NoteDto | null>;
}

export const useNote = (): INoteComposable => {
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, catchError } = useToast();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();

  const createNote = async (
    dto: Components.Schemas.CreateNoteDto,
    options: ICreateNoteOptions = {},
  ): Promise<Components.Schemas.NoteDto> => {
    return catchError(async () => {
      const note = await apiCreateNote(dto);
      await Promise.all([
        invalidateObservationTimelineQueries(queryClient, dto.schoolClassId),
        invalidateGrowthCtaQueries(queryClient),
        refreshSetupChecklist(),
      ]);
      if (!options.suppressSuccessToast) createSuccessToast();
      trackEvent(AnalyticsEvents.note.entryCreated, {
        noteCreationSource: options.noteCreationSource ?? ENoteCreationSource.Composer,
      });
      return note;
    });
  };

  const updateNote = async (
    noteId: number,
    dto: Components.Schemas.UpdateNoteDto,
  ): Promise<Components.Schemas.NoteDto> => {
    return catchError(async () => {
      const note = await apiUpdateNote({ id: noteId, dto });
      await invalidateObservationTimelineQueries(
        queryClient,
        note.schoolClassId,
        dto.date === undefined &&
          dto.schoolSubjectId === undefined &&
          dto.studentIds === undefined &&
          dto.competenceIds === undefined &&
          dto.externalMaterialIds === undefined
          ? { kind: EObservationTimelineChange.Update, entity: { type: ObservationTimelineEntryType.note, id: noteId } }
          : undefined,
      );
      if (dto.externalMaterialIds !== undefined) {
        await queryClient.invalidateQueries({ queryKey: ['supportMeasureAppRecommendations'] });
      }
      editSuccessToast();
      trackEvent(AnalyticsEvents.note.entryUpdated);
      return note;
    });
  };

  const deleteNote = async (noteId: number): Promise<void> => {
    await catchError(async () => {
      await apiDeleteNote({ id: noteId });
      await invalidateObservationTimelineQueries(queryClient, undefined, {
        kind: EObservationTimelineChange.Delete,
        entity: { type: ObservationTimelineEntryType.note, id: noteId },
      });
      await refreshSetupChecklist();
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.note.entryDeleted);
    });
  };

  const getNoteById = async (noteId: number): Promise<Components.Schemas.NoteDto | null> => {
    return catchError(() => fetchNoteById({ id: noteId }));
  };

  return {
    createNote,
    updateNote,
    deleteNote,
    getNoteById,
  };
};
